import React, { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { Typography, Box, Container } from "@mui/material";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/request/RequestStatus";
import ActionButton from "../../shared/ActionButton";
import NoticeBanner from "../../shared/NoticeBanner";
import {
  PROJECT_COLOR,
  PROJECT_COLOR_LIGHT,
  PROJECT_COLOR_DARK,
} from "../../../constants/colors";

export default function ProjectLayout() {
  const navigate = useNavigate();

  // 希望条件が入力済み
  const [contractExists, setContractExists] = useState(null);

  // 公開状態
  const [isActive, setIsActive] = useState(false);

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);

  // モーダルで確定待ちの状態（true: ON / false: OFF）
  const [pendingStatus, setPendingStatus] = useState(null);

  // 初回ロード
  useEffect(() => {
    fetch("/user_contract_request/edit.json")
      .then((res) => res.json())
      .then((data) => {
        setContractExists(data.contract_request_exists);
        setIsActive(data.recruiting_flag);
      })
      .catch((err) => console.error("Error loading request:", err));
  }, []);

  // 公開フラグ更新
  const updateRecruitingFlag = (newStatus) => {
    fetch("/user_contract_request/toggle_recruiting", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({
        user_contract_request: {
          recruiting_flag: newStatus,
        },
      }),
    }).then((res) => {
      if (res.ok) {
        // 自分の state 更新
        setIsActive(newStatus);

        // ProjectDetailPage に通知
        window.dispatchEvent(
          new CustomEvent("contract:status-updated", {
            detail: { recruiting_flag: newStatus },
          })
        );
      } else {
        alert("公開ステータスの更新に失敗しました。");
      }
    });
  };

  // ステータス切り替え（クリック時）
  const handleChangeStatus = (next) => {
    // 次にしたい状態を保存
    setPendingStatus(next);

    // ON / OFF どちらでも必ずモーダル表示
    setModalOpen(true);
  };

  // モーダル：OFF を確定
  const handleConfirmStop = () => {
    if (pendingStatus === null) return;

    // 確定した状態で更新
    updateRecruitingFlag(pendingStatus);

    // モーダルを閉じてリセット
    setModalOpen(false);
    setPendingStatus(null);
  };

  // モーダル：キャンセル
  const handleCancelStop = () => {
    setModalOpen(false);
    setPendingStatus(null);
  };

  // 遷移先
  const handleClick = () => {
    navigate(
      `/user_contract_request/edit?redirect=${window.location.pathname}`
    );
  };

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ===== 固定エリア ===== */}
      <Box
        sx={{
          position: "sticky",
          top: { xs: "44px", sm: "84px" },
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0.5,
          mx: -2,
          px: { xs: 2, sm: 3 },
          pt: { xs: 1, sm: 1.5 },
        }}
      >
        {/* --- 上段：タイトル / RequestStatus --- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1, sm: 2 },
            }}
          >
            {/* タイトル */}
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.8rem" },
                whiteSpace: "nowrap",
              }}
            >
              プロジェクト
            </Typography>

            {/* タイトル寄りの RequestStatus */}
            {contractExists && (
              <RequestStatus
                viewerType="user"
                type="project"
                isActive={isActive}
                onToggle={handleChangeStatus}
              />
            )}

            {/* NoticeBanner + ActionButton */}
            <Box
              sx={{
                ml: { sm: "auto" },
                display: "flex",
                flexDirection: { xs: "column-reverse", sm: "row" },
                gap: 1,
                width: { xs: "100%", sm: "auto" },
                alignItems: "center",
              }}
            >
              <NoticeBanner
                text={
                  contractExists
                    ? "希望条件に変更があれば、更新しておきましょう。"
                    : "希望条件を入力すると、詳細を全て閲覧できます。"
                }
              />

              <ActionButton
                onClick={handleClick}
                label={`希望条件を${contractExists ? "編集" : "入力"}`}
                icon={<ModeEditOutlinedIcon sx={{ color: PROJECT_COLOR }} />}
                showBadge={!contractExists}
                sx={{
                  bgcolor: PROJECT_COLOR,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: PROJECT_COLOR_DARK },
                }}
                badgeSx={{
                  bgcolor: PROJECT_COLOR_LIGHT,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ===== スクロール領域 ===== */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <Outlet context={{ contractExists, isActive }} />
      </Box>

      {/* --- モーダル --- */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title={isActive ? "スキルログ運営局に非公開" : "スキルログ運営局に公開"}
        message={
          isActive
            ? "非公開にすると運営局とのコンタクトができなくなります。"
            : "公開すると運営局からの連絡やプロジェクト紹介を受け取れます。"
        }
        confirmLabel={isActive ? "非公開にする" : "公開する"}
      />
    </Container>
  );
}
